/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import AgencyPlaylisterControlAuditEntity from 'Models/Security/Acl/AgencyPlaylisterControlAuditEntity';
import MustardManagementControlAuditEntity from 'Models/Security/Acl/MustardManagementControlAuditEntity';
import VisitorsControlAuditEntity from 'Models/Security/Acl/VisitorsControlAuditEntity';
import ApplicationUserControlAuditEntity from 'Models/Security/Acl/ApplicationUserControlAuditEntity';
import MustardAdminControlAuditEntity from 'Models/Security/Acl/MustardAdminControlAuditEntity';
import AgencyAdminControlAuditEntity from 'Models/Security/Acl/AgencyAdminControlAuditEntity';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IControlAuditEntityAttributes extends IModelAttributes {
	overrideStart: Date;
	overrideStop: Date;
	controlAuditType: Enums.controlType;

	createdById?: string;
	createdBy?: Models.ApplicationUserEntity | Models.IApplicationUserEntityAttributes;
	moodId?: string;
	mood?: Models.MoodEntity | Models.IMoodEntityAttributes;
	trackId?: string;
	track?: Models.TrackEntity | Models.ITrackEntityAttributes;
	controllingId?: string;
	controlling?: Models.ZoneEntity | Models.IZoneEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('ControlAuditEntity', 'Control Audit')
// % protected region % [Customise your entity metadata here] end
export default class ControlAuditEntity extends Model
	implements IControlAuditEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new AgencyPlaylisterControlAuditEntity(),
		new MustardManagementControlAuditEntity(),
		new VisitorsControlAuditEntity(),
		new ApplicationUserControlAuditEntity(),
		new MustardAdminControlAuditEntity(),
		new AgencyAdminControlAuditEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Override Start'] off begin
	@observable
	@attribute<ControlAuditEntity, Date>()
	@CRUD({
		name: 'Override Start',
		displayType: 'datetimepicker',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public overrideStart: Date;
	// % protected region % [Modify props to the crud options here for attribute 'Override Start'] end

	// % protected region % [Modify props to the crud options here for attribute 'Override Stop'] off begin
	@observable
	@attribute<ControlAuditEntity, Date>()
	@CRUD({
		name: 'Override Stop',
		displayType: 'datetimepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public overrideStop: Date;
	// % protected region % [Modify props to the crud options here for attribute 'Override Stop'] end

	// % protected region % [Modify props to the crud options here for attribute 'Control Audit Type'] off begin
	@observable
	@attribute<ControlAuditEntity, Enums.controlType>()
	@CRUD({
		name: 'Control Audit Type',
		displayType: 'enum-combobox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.controlTypeOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.controlTypeOptions),
		displayFunction: (attr: Enums.controlType) => Enums.controlTypeOptions[attr],
	})
	public controlAuditType: Enums.controlType;
	// % protected region % [Modify props to the crud options here for attribute 'Control Audit Type'] end

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Created By'] off begin
		name: 'Created By',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.ApplicationUserEntity,
		// % protected region % [Modify props to the crud options here for reference 'Created By'] end
	})
	public createdById?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public createdBy: Models.ApplicationUserEntity;

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Mood'] off begin
		name: 'Mood',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.MoodEntity,
		// % protected region % [Modify props to the crud options here for reference 'Mood'] end
	})
	public moodId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public mood: Models.MoodEntity;

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Track'] off begin
		name: 'Track',
		displayType: 'reference-combobox',
		order: 60,
		referenceTypeFunc: () => Models.TrackEntity,
		// % protected region % [Modify props to the crud options here for reference 'Track'] end
	})
	public trackId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public track: Models.TrackEntity;

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Controlling'] off begin
		name: 'Controlling',
		displayType: 'reference-combobox',
		order: 70,
		referenceTypeFunc: () => Models.ZoneEntity,
		// % protected region % [Modify props to the crud options here for reference 'Controlling'] end
	})
	public controllingId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public controlling: Models.ZoneEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IControlAuditEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IControlAuditEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.overrideStart !== undefined) {
				if (attributes.overrideStart === null) {
					this.overrideStart = attributes.overrideStart;
				} else {
					this.overrideStart = moment(attributes.overrideStart).toDate();
				}
			}
			if (attributes.overrideStop !== undefined) {
				if (attributes.overrideStop === null) {
					this.overrideStop = attributes.overrideStop;
				} else {
					this.overrideStop = moment(attributes.overrideStop).toDate();
				}
			}
			if (attributes.controlAuditType !== undefined) {
				this.controlAuditType = attributes.controlAuditType;
			}
			if (attributes.createdById !== undefined) {
				this.createdById = attributes.createdById;
			}
			if (attributes.createdBy !== undefined) {
				if (attributes.createdBy === null) {
					this.createdBy = attributes.createdBy;
				} else if (attributes.createdBy instanceof Models.ApplicationUserEntity) {
					this.createdBy = attributes.createdBy;
					this.createdById = attributes.createdBy.id;
				} else {
					this.createdBy = new Models.ApplicationUserEntity(attributes.createdBy);
					this.createdById = this.createdBy.id;
				}
			}
			if (attributes.moodId !== undefined) {
				this.moodId = attributes.moodId;
			}
			if (attributes.mood !== undefined) {
				if (attributes.mood === null) {
					this.mood = attributes.mood;
				} else if (attributes.mood instanceof Models.MoodEntity) {
					this.mood = attributes.mood;
					this.moodId = attributes.mood.id;
				} else {
					this.mood = new Models.MoodEntity(attributes.mood);
					this.moodId = this.mood.id;
				}
			}
			if (attributes.trackId !== undefined) {
				this.trackId = attributes.trackId;
			}
			if (attributes.track !== undefined) {
				if (attributes.track === null) {
					this.track = attributes.track;
				} else if (attributes.track instanceof Models.TrackEntity) {
					this.track = attributes.track;
					this.trackId = attributes.track.id;
				} else {
					this.track = new Models.TrackEntity(attributes.track);
					this.trackId = this.track.id;
				}
			}
			if (attributes.controllingId !== undefined) {
				this.controllingId = attributes.controllingId;
			}
			if (attributes.controlling !== undefined) {
				if (attributes.controlling === null) {
					this.controlling = attributes.controlling;
				} else if (attributes.controlling instanceof Models.ZoneEntity) {
					this.controlling = attributes.controlling;
					this.controllingId = attributes.controlling.id;
				} else {
					this.controlling = new Models.ZoneEntity(attributes.controlling);
					this.controllingId = this.controlling.id;
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		createdBy {
			${Models.ApplicationUserEntity.getAttributes().join('\n')}
			${Models.ApplicationUserEntity.getFiles().map(f => f.name).join('\n')}
		}
		mood {
			${Models.MoodEntity.getAttributes().join('\n')}
		}
		track {
			${Models.TrackEntity.getAttributes().join('\n')}
			${Models.TrackEntity.getFiles().map(f => f.name).join('\n')}
		}
		controlling {
			${Models.ZoneEntity.getAttributes().join('\n')}
			${Models.ZoneEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] off begin
		return this.id;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ControlAuditEntity.prototype, 'created');
CRUD(modifiedAttr)(ControlAuditEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
